import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardList, LayoutDashboard, Loader2, UserCheck } from "lucide-react";

const STATUSES = ["all", "submitted", "under_review", "sanctioned", "rejected"];

export default function TaskManagement() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, executives: [], can_manage: false });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [assignee, setAssignee] = useState("all");
  const [page, setPage] = useState(1);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/tasks", { params: { status, assignee: assignee === "all" ? undefined : assignee, page, limit } });
      setData(res.data);
    } catch { toast.error("Failed to load tasks"); }
    finally { setLoading(false); }
  }, [status, assignee, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status, assignee]);

  const assign = async (app, userId) => {
    try {
      await api.post(`/admin/applications/${app.id}/assign`, { user_id: userId === "unassign" ? null : userId });
      toast.success(userId === "unassign" ? "Unassigned" : "Application assigned");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to assign"); }
  };

  return (
    <div className="min-h-screen bg-[#f8faf8]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-head font-extrabold text-2xl sm:text-3xl text-emerald-950 flex items-center gap-2"><ClipboardList className="h-7 w-7 text-emerald-700" /> Task Assignment</h1>
            <p className="mt-1.5 text-slate-500">Assign applications to field executives for verification and follow-up.</p>
          </div>
          <Button variant="ghost" className="rounded-full text-emerald-700 h-10" onClick={() => navigate("/admin")} data-testid="task-back-console"><LayoutDashboard className="h-4 w-4 mr-1.5" /> Console</Button>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 items-center">
          <Select value={status} onValueChange={setStatus}><SelectTrigger className="h-10 rounded-full w-44 bg-white" data-testid="task-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s.replace("_", " ")}</SelectItem>)}</SelectContent></Select>
          <Select value={assignee} onValueChange={setAssignee}><SelectTrigger className="h-10 rounded-full w-52 bg-white" data-testid="task-assignee-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All assignments</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {data.executives.map((e) => <SelectItem key={e.id} value={e.id}>{e.name || e.email}</SelectItem>)}
            </SelectContent></Select>
        </div>

        <Card className="mt-5 rounded-3xl border-emerald-100 overflow-hidden">
          {loading ? (
            <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
          ) : data.items.length === 0 ? (
            <div className="py-16 text-center text-slate-400" data-testid="task-empty">No applications found.</div>
          ) : (
            <div className="divide-y divide-emerald-50" data-testid="task-table">
              {data.items.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-4 px-5 py-4">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-800 truncate">{a.applicant?.full_name || "—"} <span className="text-xs text-slate-400">· {a.number}</span></div>
                    <div className="text-xs text-slate-400 mt-0.5">{a.scheme_name}</div>
                  </div>
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200 capitalize">{(a.status || "").replace("_", " ")}</span>
                  <div className="w-56">
                    {data.can_manage ? (
                      <Select value={a.assigned_to || "unassign"} onValueChange={(v) => assign(a, v)}>
                        <SelectTrigger className="h-9 rounded-full bg-white text-xs" data-testid={`task-assign-${a.id}`}><SelectValue placeholder="Assign to..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassign">Unassigned</SelectItem>
                          {data.executives.map((e) => <SelectItem key={e.id} value={e.id}>{e.name || e.email}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs text-slate-500 inline-flex items-center gap-1"><UserCheck className="h-3 w-3 text-emerald-600" /> {a.assignee_name || "Unassigned"}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && data.total > limit && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-emerald-100">
              <span className="text-xs text-slate-500">Showing {(page - 1) * limit + 1}–{Math.min(page * limit, data.total)} of {data.total}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-full border-emerald-200 text-emerald-700 h-8" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="task-prev">Previous</Button>
                <Button variant="outline" size="sm" className="rounded-full border-emerald-200 text-emerald-700 h-8" disabled={page * limit >= data.total} onClick={() => setPage((p) => p + 1)} data-testid="task-next">Next</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
